import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { movingTruckCLPSelector } from 'store/selectors/landing';
import { usePageViewTracking } from 'components/core/library/tracking-utils/tracking/withPageViewTracking';

import { BrandBanner } from 'components/brand/BrandBanner';
import { CallCenterBanner } from 'components/contact/CallCenterBanner';
import { FacilityRow } from 'components/search/FacilityRow';
import { LandingHero } from 'components/landing/LandingHero';
import { Breadcrumbs, Crumb } from 'components/core/Breadcrumbs';
import { TruckPromoCards } from 'components/brand/TruckPromoCards';
import { MovingTruckContent } from 'components/landing/MovingTruckContent';
import { MovingTruckBlogContent } from 'components/landing/MovingTruckBlogContent';
import { TruckSearchBlock } from 'components/landing/TruckSearchBlock';
import { getTollfreePhoneNumber } from 'store/pageMeta/slice';

import './movingTruckCLPPage.scss';

export function MovingTruckCLPPage(props) {
	const dispatch = useDispatch();
	const selectorProps = useSelector((state) => movingTruckCLPSelector(state));
	const actions = useMemo(() => bindActionCreators({
		getTollfreePhoneNumber,
	}, dispatch), [dispatch]);

	usePageViewTracking(PAGE_TYPES.CITY_LANDING, { ...props, ...selectorProps, actions });

	const {
		bellhopsURL = '',
		className,
		isBellhopsMarket = false,
		location = {},
		penskeURL = '',
		phone = {},
		truckFacilities,
		truckResultsURL = '',
		content = {},
		canonicalUrl,
	} = { ...props, ...selectorProps };
	return (
		<div
			className={classnames(
				'moving-truck-landing-page psp-landing-page',
				className
			)}
		>
			<LandingHero
				dark
				headline={`Moving truck rentals in ${location.city}, ${location.state}`}
				subheading="Get the best deals on moving truck rentals."
				withSearchBox={false}
			/>
			<TruckPromoCards
				isBellhopsMarket={isBellhopsMarket}
				bellhopsURL={bellhopsURL}
				penskeURL={penskeURL}
				location={location}
			/>

			<BrandBanner
				phone={phone}
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
			/>

			{/* Search Results if location is found */}
			{!!location.city && (
				<div className="section-content local-results-section">
					{!!truckFacilities && truckFacilities.length > 0 && (
						<FacilityRow
							facilities={truckFacilities}
							cityURL={truckResultsURL}
							location={location}
							className="truck-facility-row"
							storageType="storage with moving trucks"
							headline={`Moving trucks with storage rental in ${location.city}, ${location.state}`}
							subheading={`Get a moving truck when you rent a storage unit with any of these ${location.city}, ${location.state} facilities`}
							linkText={`View more storage facilities with moving trucks in ${location.city}`}
							withPhone
							withUnitsAndPrice
							withButtonLink={false}
							noFollowCityURL
						/>
					)}
				</div>
			)}
			{/* Blog Links */}
			{/* TODO: check to BlogPosts component */}
			<div className="section-content static-content">
				<MovingTruckBlogContent city={location.city} />
			</div>
			{/* End Blog Links */}

			<TruckSearchBlock
				headerText={`Looking for storage with moving truck rentals in ${location.city}, ${location.state}?`}
				buttonText="See storage with trucks"
				location={location}
				withSearch={false}
			/>

			<div className="section-content static-content">
				<MovingTruckContent
					content={content}
					city={location.city}
				/>
			</div>

			<CallCenterBanner
				phone={phone}
				getTollfreePhoneNumber={actions.getTollfreePhoneNumber}
				locationString={`${location.city}, ${location.state}`}
			/>

			<div className="section-content static-content">
				<Breadcrumbs
					crumbs={[
						<Crumb
							key="moving-truck"
							href="/moving-truck-rental.html"
							label="Moving Trucks"
						/>,
						<Crumb
							key="moving-truck-city"
							isLast
							href={canonicalUrl}
							label={location.city}
						/>,
					]}
				/>
			</div>
		</div>
	);
}

MovingTruckCLPPage.propTypes = {
	location: PropTypes.object,
	truckResultsURL: PropTypes.string,
	bellhopsURL: PropTypes.string,
	penskeURL: PropTypes.string,
	isBellhopsMarket: PropTypes.bool,
	phone: PropTypes.object,
	className: PropTypes.string,
	truckFacilities: PropTypes.arrayOf(PropTypes.object),
	content: PropTypes.object,
	canonicalUrl: PropTypes.string,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func,
	}).isRequired,
};

export default MovingTruckCLPPage;
